import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  RobotMapTool,
  generateProjection,
} from '@warehouse/utils/RobotMapTool';
import './index.scss';
import MapUI from '../MapUI';
import BoardApi from '@warehouse/bussinessApi/board';
import { HttpStatusCode } from '@/fetch/core/constant';
import { message, Checkbox } from 'antd';
import {
  iconHighlightMap,
  iconMap,
  LayerId,
  OFFLINE_ICON,
} from '@warehouse/utils/constant';
import MapLegend from '@warehouse/components/MapLegend';
enum MapBusinessType {
  POINT = 'POINT',
  LINE = 'LINE',
  AREA = 'AREA',
  POINT_NAME = 'POINT_NAME',
}

const mapBusiness = [
  {
    value: MapBusinessType.POINT,
    label: '点',
    layerId: LayerId.POINT_MAP,
  },
  {
    value: MapBusinessType.LINE,
    label: '线',
    layerId: LayerId.ROAD_MAP,
  },
  {
    value: MapBusinessType.AREA,
    label: '区域',
    layerId: LayerId.AREA_MAP,
  },
  {
    value: MapBusinessType.POINT_NAME,
    label: '点名称',
    layerId: LayerId.POINT_NAME,
  },
];
const fetchApi = new BoardApi();
const getLayerZIndex = (layerId: LayerId) => {
  switch (layerId) {
    case LayerId.STOP_POINT:
      return 70; // 停靠点label需要在最上层
    case LayerId.DEVICE_POINT:
      return 50; // 设备点位
    case LayerId.GEOMETRY:
      return 40; // 几何路径
    case LayerId.POINT_NAME:
      return 60; // 点名称
    case LayerId.ROAD_MAP:
      return 30; // 道路
    case LayerId.POINT_MAP:
      return 25; // 地图点
    case LayerId.AREA_MAP:
      return 20; // 区域地图
    default:
      return 50; // 默认值
  }
};
const MapInfo = (props: {
  mapId: string;
  mapVersion: string;
  defaultZoom?: number;
  showTools?: boolean;
  curDeviceName?: string;
  timeStamp?: number;
  stopList?: {
    stopId: number;
    stopName: string;
    x: number;
    y: number;
  }[];
  geometryList?: any[];
  devicePointList: {
    x: number;
    y: number;
    deviceName: string;
    remarkName?: string;
    alarmState: 'CRITICAL' | 'SEVERITY' | 'NORMAL';
    onlineStatus?: number;
  }[];
  onNavigateToDevice?: (deviceName: string) => void;
  handleDeviceClick?: (
    deviceName?: string,
    type?: 'selected' | 'cancel'
  ) => void;
  labelStyle?: any;
}) => {
  const mapRef = useRef<any>(null);
  const mapContainer = useRef<any>();
  const [allMapInfo, setAllMapInfo] = useState<any>();
  const selectedDeviceIdRef = useRef<string | null>(
    props.curDeviceName || null
  );
  const showingLabelDeviceIdRef = useRef<string | null>(null);
  const fetchMapElement = async () => {
    if (!props.mapId || !props.mapVersion) {
      // message.error('mapId或mapVersion为空');
      return;
    }
    const res = await fetchApi.getMapInfo({
      version: props.mapVersion,
      number: props.mapId,
    });
    if (res?.code === HttpStatusCode.Success) {
      setAllMapInfo(res?.data);
      if (!mapRef.current) {
        drawMap(res?.data?.mapInfo);
      }
    } else {
      message.error(res?.message);
    }
  };

  const drawMap = (mapInfo: any) => {
    const {
      width,
      height,
      offsetBottomX,
      offsetBottomY,
      offsetTopX,
      offsetTopY,
      origin,
      resolution,
    } = mapInfo || {};
    if (!origin) {
      return;
    }
    const originX = origin?.coordinates[0];
    const originY = origin?.coordinates[1];
    const MAP_ROTATION = (174 * Math.PI) / 180;
    const extent: [number, number, number, number] = [
      originX + (offsetBottomX || 0), // minX (左下角X)
      originY + (offsetBottomY || 0), // minY (左下角Y)
      originX + width * resolution + (offsetTopX || 0), // maxX (右上角X)
      originY + height * resolution + (offsetTopY || 0), // maxY (右上角Y)
    ];
    const projection = generateProjection(extent);
    mapRef.current = new RobotMapTool({
      container: mapContainer.current,
      projection: projection,
      center: [0, 0],
      rotation: MAP_ROTATION,
      defaultZoom: props.defaultZoom || 1,
    });
    mapRef.current.createImgLayer(
      mapInfo.jpgFileUrl,
      projection,
      extent,
      LayerId.BASE_IMAGE_MAP
    );
  };
  // yaw角转换成图标旋转角度
  const getIconRotation = (yaw: number) => {
    if (yaw === undefined || yaw === null) return 0;

    // 将弧度转换为角度
    let degrees = (yaw * 180) / Math.PI;

    // 修正垂直方向：由于地图坐标系Y轴方向与实际相反，需要取负值
    degrees = -degrees;

    // 调整角度：
    // 1. yaw=0 时，设备正确朝向是水平向左
    // 2. 图标默认箭头水平朝右
    // 3. 所以需要旋转180度来对齐：最终角度 = -yaw角度 + 180度
    degrees = degrees + 180;

    // 确保角度在 -180 到 180 范围内
    while (degrees > 180) {
      degrees -= 360;
    }
    while (degrees < -180) {
      degrees += 360;
    }

    return degrees;
  };
  const createLayer = (layerId: LayerId) => {
    let dataList;
    switch (layerId) {
      case LayerId.AREA_MAP:
        dataList = allMapInfo?.areaInfoList?.map((val) => {
          return {
            type: 'Polygon',
            coordinates: val.points.coordinates,
            id: val.id,
          };
        });
        break;
      case LayerId.POINT_MAP:
        dataList = allMapInfo?.pointInfoList?.map((val) => {
          return {
            type: 'Point',
            coordinates: [val.pointX, val.pointY],
            id: val.id,
            iconUrl: require('@warehouse/assets/common/map_marker.png'),
          };
        });
        break;
      case LayerId.ROAD_MAP:
        dataList = allMapInfo?.voronoiInfoList?.map((val) => {
          return {
            type: 'LineString',
            coordinates: val.edge.coordinates,
            id: val.id,
          };
        });
        break;
      case LayerId.POINT_NAME:
        dataList = allMapInfo?.pointInfoList?.map((val) => {
          return {
            type: 'Point',
            coordinates: [val.pointX, val.pointY],
            id: val.id + 'name',
            name: val.name,
          };
        });
        break;
      case LayerId.DEVICE_POINT:
        dataList = props?.devicePointList?.map((item: any) => {
          const isSelected = selectedDeviceIdRef.current === item?.deviceName;
          return {
            type: 'Point',
            coordinates: [item?.x, item?.y],
            id: item?.deviceName + item?.alarmState,
            deviceName: item?.deviceName,
            remarkName: item?.remarkName,
            labelText: props.onNavigateToDevice
              ? `车牌号：${item?.remarkName} >`
              : `车牌号：${item?.remarkName}`,
            onlineStatus: item?.onlineStatus,
            iconUrl:
              item?.onlineStatus == 0
                ? OFFLINE_ICON
                : isSelected
                  ? iconHighlightMap.get(item?.alarmState)
                  : iconMap.get(item?.alarmState),
            otherStyle: {
              width: item?.onlineStatus == 0 ? 44 : isSelected ? 67 : 44,
              height: item?.onlineStatus == 0 ? 37 : isSelected ? 67 : 37,
              rotation: item?.yaw ? getIconRotation(item?.yaw) : 0,
            },
            isSelected: isSelected,
          };
        });
        break;
      case LayerId.STOP_POINT:
        dataList = props?.stopList?.map((item: any) => {
          const pointData = {
            type: 'Point',
            coordinates: [item.x, item.y],
            id: item.stopId,
            name: item.stopName,
            iconUrl: require('@warehouse/assets/common/stop_point.png'),
            otherStyle: {
              width: 18,
              height: 18,
            },
            labelStyle: {
              text: item.stopName,
              showLabel: true,
            },
          };
          return pointData;
        });
        break;
      case LayerId.GEOMETRY:
        dataList = [
          {
            type: 'LineString',
            coordinates: props.geometryList,
            id: 'LineString',
            // 添加线条样式配置
            lineStyle: {
              color: '#12B35D',
              width: 8,
              borderColor: '#0B723B',
              borderWidth: 1, // 边框宽度
            },
          },
        ];
        break;
      default:
        break;
    }

    // 为设备点位图层添加点击事件
    const onFeatureClick =
      layerId === LayerId.DEVICE_POINT ? handleDeviceClick : undefined;
    mapRef.current?.createVectorLayer(
      {
        zIndex: getLayerZIndex(layerId),
      },
      layerId,
      dataList,
      onFeatureClick
    );
  };

  const handleChangeBusiness = (item: any, checked: boolean) => {
    if (!mapRef.current?.getLayer(item.layerId)) {
      createLayer(item.layerId);
    }
    mapRef.current?.changeLayerVisible(item.layerId, checked);
  };

  const clearPoint = (layerId: LayerId) => {
    mapRef.current.removeLayerById(layerId);
  };

  const handleDeviceClick = (
    _feature: any,
    deviceName?: string,
    remarkName?: string
  ) => {
    let type: 'selected' | 'cancel' | undefined = undefined;
    if (deviceName && mapRef.current) {
      const currentSelectedId = selectedDeviceIdRef.current;
      // 处理设备选中状态（非单车页才有高亮效果）
      if (!props.curDeviceName) {
        if (currentSelectedId === deviceName) {
          selectedDeviceIdRef.current = null;
          mapRef.current.updateDeviceSelection(deviceName, false);
          mapRef.current.removeScheduledPath();
          clearPoint(LayerId.GEOMETRY);
          clearPoint(LayerId.STOP_POINT);
        } else {
          // 先取消之前选中的设备
          if (currentSelectedId) {
            mapRef.current.updateDeviceSelection(currentSelectedId, false);
            mapRef.current.updateDeviceSelection(deviceName, false);
            mapRef.current.removeScheduledPath();
            clearPoint(LayerId.GEOMETRY);
            clearPoint(LayerId.STOP_POINT);
          }
          // 选中新设备
          selectedDeviceIdRef.current = deviceName;
          mapRef.current.updateDeviceSelection(deviceName, true);
        }

        type = currentSelectedId === deviceName ? 'cancel' : 'selected';
      }

      // 处理label显示（所有页面都显示label）
      const currentShowingLabel = showingLabelDeviceIdRef.current;
      if (currentShowingLabel === deviceName) {
        showingLabelDeviceIdRef.current = null;
        mapRef.current.hideDeviceLabel(deviceName);
      } else {
        if (currentShowingLabel) {
          mapRef.current.hideDeviceLabel(currentShowingLabel);
        }
        showingLabelDeviceIdRef.current = deviceName;
        mapRef.current.showDeviceLabel(
          deviceName,
          remarkName,
          handleNavigateToDevice
        );
      }
    }
    props.handleDeviceClick && props.handleDeviceClick(deviceName, type);
  };

  // 处理导航到设备详情页
  const handleNavigateToDevice = (deviceName: string) => {
    if (props.onNavigateToDevice) {
      props.onNavigateToDevice(deviceName);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (mapRef.current) {
        clearInterval(interval);
        clearPoint(LayerId.DEVICE_POINT);
        createLayer(LayerId.DEVICE_POINT);
      }
    }, 17);
    return () => {
      clearInterval(interval);
    };
  }, [JSON.stringify(props.devicePointList)]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (mapRef.current) {
        clearInterval(interval);
        mapRef.current.removeScheduledPath();
        clearPoint(LayerId.GEOMETRY);
        mapRef.current.drawScheduledPath(props.geometryList || []);
      }
    }, 17);
    return () => {
      clearInterval(interval);
    };
  }, [JSON.stringify(props.geometryList), props.timeStamp]);

  useEffect(() => {
    fetchMapElement();
  }, [props.mapId, props.mapVersion]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (mapRef.current) {
        clearInterval(interval);
        clearPoint(LayerId.STOP_POINT);
        createLayer(LayerId.STOP_POINT);
      }
    }, 17);
    return () => {
      clearInterval(interval);
    };
  }, [JSON.stringify(props.stopList), props.timeStamp]);

  return (
    <div className="map-info">
      <MapUI ref={mapContainer} />
      {props.showTools && (
        <>
          <MapLegend
            title="仓内元素"
            defaultCollapsed={false}
            className="warehouse-legend"
          >
            <Checkbox.Group
              options={mapBusiness.map((item) => ({
                label: <span style={{ color: '#ffffff' }}>{item.label}</span>,
                value: item.value,
              }))}
              onChange={(checkedValues) => {
                mapBusiness.forEach((item) => {
                  const isChecked = checkedValues.includes(item.value);
                  handleChangeBusiness(item, isChecked);
                });
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                border: 'none',
              }}
            />
          </MapLegend>
          <MapLegend
            title="车辆图例"
            defaultCollapsed={false}
            className="status-legend"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#4A90E2',
                    borderRadius: '50%',
                    marginRight: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                />
                <span
                  style={{
                    color: '#fff',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  正常
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#F5A623',
                    borderRadius: '50%',
                    marginRight: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                />
                <span
                  style={{
                    color: '#fff',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  车辆遇阻
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#D0021B',
                    borderRadius: '50%',
                    marginRight: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                />
                <span
                  style={{
                    color: '#fff',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  碰撞或任务超时
                </span>
              </div>
            </div>
          </MapLegend>
        </>
      )}
    </div>
  );
};

export default React.memo(MapInfo);
